import React, { useCallback, useEffect, useMemo, useState } from 'react';

// Shared table sorting: one comparator, one hook, one header cell for every table in the portal.
// Names sort by last name, then first name (case-insensitive, locale-aware); blanks always sort last.

const collator = new Intl.Collator('en', { sensitivity: 'base', numeric: true });
const STORAGE_PREFIX = 'vp.tableSort.';

export const isBlank = (v) =>
    v === null || v === undefined || (typeof v === 'string' && v.trim() === '') || (typeof v === 'number' && Number.isNaN(v));

const US_DATE = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;

/** Milliseconds for a Date / ISO string / MM/DD/YYYY string / epoch number; null when missing or unparseable. */
export const toTime = (v) => {
    if (isBlank(v)) return null;
    if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v.getTime();
    if (typeof v === 'number') return v;
    if (typeof v === 'object' && typeof v.valueOf === 'function' && typeof v.valueOf() === 'number') return v.valueOf();
    const s = String(v).trim();
    const us = s.match(US_DATE);
    if (us) return Date.UTC(+us[3], +us[1] - 1, +us[2]);
    const t = Date.parse(s);
    return Number.isNaN(t) ? null : t;
};

const toNumber = (v) => {
    if (isBlank(v) || typeof v === 'boolean') return typeof v === 'boolean' ? Number(v) : null;
    const n = typeof v === 'number' ? v : Number(String(v).replace(/[$,\s]/g, ''));
    return Number.isNaN(n) ? null : n;
};

/** {last, first} from a person-like object (firstname/lastname). */
export const personName = (p) => ({ last: p ? p.lastname : undefined, first: p ? p.firstname : undefined });

/**
 * {last, first} from a display string. "Last, First" is always honoured; otherwise the string is read in
 * `order` ('first-last' -> last word is the last name, 'last-first' -> first word is the last name).
 */
export const nameFromString = (s, order = 'first-last') => {
    if (isBlank(s)) return { last: undefined, first: undefined };
    const str = String(s).trim();
    if (str.includes(',')) {
        const [last, ...rest] = str.split(',');
        return { last: last.trim(), first: rest.join(',').trim() };
    }
    const parts = str.split(/\s+/);
    if (parts.length === 1) return { last: parts[0], first: '' };
    if (order === 'last-first') return { last: parts[0], first: parts.slice(1).join(' ') };
    return { last: parts[parts.length - 1], first: parts.slice(0, -1).join(' ') };
};

const nameParts = (v) => {
    if (isBlank(v)) return null;
    const n = typeof v === 'string' ? nameFromString(v) : v;
    const last = isBlank(n.last) ? '' : String(n.last).trim();
    const first = isBlank(n.first) ? '' : String(n.first).trim();
    return last || first ? [last, first] : null;
};

const normalise = (type, v) => {
    switch (type) {
        case 'name': return nameParts(v);
        case 'date': return toTime(v);
        case 'number': return toNumber(v);
        case 'boolean': return isBlank(v) ? null : (v ? 1 : 0);
        default: return isBlank(v) ? null : String(v).trim();
    }
};

/** Compare two non-blank normalised values of `type` ascending. */
const compareNormalised = (type, a, b) => {
    if (type === 'name') {
        if (!a[0] !== !b[0]) return a[0] ? -1 : 1; // missing last name after present ones
        return collator.compare(a[0], b[0]) || collator.compare(a[1], b[1]);
    }
    if (type === 'date' || type === 'number' || type === 'boolean') return a - b;
    return collator.compare(a, b);
};

/**
 * Comparator over raw values. Blank values sort last for BOTH directions; `dir` is 'asc' | 'desc'.
 * Types: 'name' ({last, first} or display string), 'date', 'number', 'boolean', 'string'.
 */
export const compareValues = (a, b, type = 'string', dir = 'asc') => {
    const x = normalise(type, a);
    const y = normalise(type, b);
    if (x === null && y === null) return 0;
    if (x === null) return 1;
    if (y === null) return -1;
    if (type === 'name' && !x[0] !== !y[0]) return x[0] ? -1 : 1; // missing last name stays last in both directions
    const c = compareNormalised(type, x, y);
    return dir === 'desc' ? -c : c;
};

/** Stable sort of `rows` by `column` = { type, value: row => raw }. Returns a new array. */
export const sortRows = (rows, column, dir = 'asc') => {
    const list = Array.isArray(rows) ? rows : [];
    if (!column) return list.slice();
    const get = column.value || ((r) => r);
    return list
        .map((row, i) => ({ row, i, v: get(row) }))
        .sort((p, q) => compareValues(p.v, q.v, column.type || 'string', dir) || p.i - q.i)
        .map((e) => e.row);
};

const readStored = (id) => {
    if (!id) return null;
    try {
        const raw = window.localStorage.getItem(STORAGE_PREFIX + id);
        const v = raw ? JSON.parse(raw) : null;
        return v && typeof v.key === 'string' && (v.dir === 'asc' || v.dir === 'desc') ? v : null;
    } catch (e) {
        return null;
    }
};

const writeStored = (id, v) => {
    if (!id) return;
    try {
        window.localStorage.setItem(STORAGE_PREFIX + id, JSON.stringify(v));
    } catch (e) {
        // storage unavailable (private mode / quota): sorting still works for this visit
    }
};

/**
 * useSortableTable(rows, { id, columns, defaultSort, manual })
 *  - columns: { [key]: { type, value: row => raw, defaultDir? } }
 *  - defaultSort: { key, dir } (optional; no sort = original order)
 *  - manual: true for server-sorted tables (rows are returned as given; read sortKey/sortDir and refetch)
 * The last sort is remembered per table id in localStorage.
 */
export const useSortableTable = (rows, { id, columns = {}, defaultSort = null, manual = false } = {}) => {
    const [sort, setSort] = useState(() => {
        const stored = readStored(id);
        if (stored && columns[stored.key]) return stored;
        return defaultSort && columns[defaultSort.key] ? { key: defaultSort.key, dir: defaultSort.dir || 'asc' } : null;
    });

    useEffect(() => { if (sort) writeStored(id, sort); }, [id, sort]);

    const requestSort = useCallback((key) => {
        setSort((s) => {
            if (s && s.key === key) return { key, dir: s.dir === 'asc' ? 'desc' : 'asc' };
            return { key, dir: (columns[key] && columns[key].defaultDir) || 'asc' };
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const sorted = useMemo(() => {
        const list = Array.isArray(rows) ? rows : [];
        if (manual || !sort || !columns[sort.key]) return list;
        return sortRows(list, columns[sort.key], sort.dir);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows, manual, sort && sort.key, sort && sort.dir]);

    return {
        rows: sorted,
        sortKey: sort ? sort.key : null,
        sortDir: sort ? sort.dir : null,
        requestSort,
        setSort,
    };
};

const ARROWS = { asc: '\u25B2', desc: '\u25BC', none: '\u21C5' };

/** Clickable, keyboard-accessible header cell. `sort` is the object returned by useSortableTable. */
export const SortableTh = ({ sortKey, sort, children, className, style, title, ...rest }) => {
    const active = sort && sort.sortKey === sortKey;
    const dir = active ? sort.sortDir : 'none';
    const ariaSort = active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none';
    const label = typeof children === 'string' ? children.trim() : '';
    return (
        <th className={className} style={style} aria-sort={ariaSort} {...rest}>
            <button
                type="button"
                className="sortable-th-btn"
                onClick={() => sort && sort.requestSort(sortKey)}
                title={title || (label ? `Sort by ${label}` : 'Sort')}
                data-sort-key={sortKey}
            >
                {children}
                <span className={`sort-arrow${active ? ' sort-arrow-active' : ''}`} aria-hidden="true">{ARROWS[dir]}</span>
            </button>
        </th>
    );
};

/** Query-string fragment for server-sorted lists: "sortBy=name&sortDir=asc" (empty when unsorted). */
export const sortQuery = (sortKey, sortDir) =>
    sortKey ? `sortBy=${encodeURIComponent(sortKey)}&sortDir=${sortDir === 'desc' ? 'desc' : 'asc'}` : '';

export default SortableTh;

/** { sortBy, sortDir } for a server-sorted list when the active column is in `serverKeys`, else null (legacy order). */
export const serverSortParams = (tableSort, serverKeys) =>
    tableSort && tableSort.sortKey && serverKeys.includes(tableSort.sortKey)
        ? { sortBy: tableSort.sortKey, sortDir: tableSort.sortDir }
        : null;
